import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Home, Users, Calendar as CalendarIcon, RefreshCw } from 'lucide-react';
import { getStatusStyle, shiftDurationHours } from './calendarUtils';

// Right-side drawer with SIL house details, residents, and shifts in view.
export function CalendarSILHouseDrawer({ houseId, allShifts, open, onClose, onShiftClick }) {
  const [house, setHouse] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !houseId) return;
    setLoading(true);
    Promise.all([
      api.get(`/houses/${houseId}`).catch(() => ({ data: { data: null } })),
      api.get(`/accommodation/by-house/${houseId}`).catch(() => ({ data: [] })),
    ]).then(([hRes, aRes]) => {
      setHouse(hRes.data?.data || hRes.data || null);
      const allocs = Array.isArray(aRes.data) ? aRes.data : (aRes.data?.items || aRes.data?.data || []);
      setAllocations(allocs.filter(a => a.status === 'ACTIVE'));
    }).finally(() => setLoading(false));
  }, [open, houseId]);

  const houseShifts = useMemo(() => {
    return allShifts.filter(s => (s.house_id === houseId || s._sil_house_id === houseId)).sort((a, b) => `${a.date} ${a.start_time}`.localeCompare(`${b.date} ${b.start_time}`));
  }, [allShifts, houseId]);

  const totalHours = houseShifts.reduce((acc, s) => acc + shiftDurationHours(s), 0);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[460px] sm:w-[500px] overflow-y-auto" data-testid="sil-house-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Home className="w-4 h-4 text-emerald-600" /> SIL House Details</SheetTitle>
          <SheetDescription>House details, current residents, and shifts in the visible date range</SheetDescription>
        </SheetHeader>
        {loading ? (
          <div className="flex items-center justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : house ? (
          <div className="space-y-4 mt-4">
            <Card>
              <CardContent className="p-3 space-y-2 text-sm">
                <div><p className="text-muted-foreground text-xs">Name</p><p className="font-medium">{house.name}</p></div>
                <div><p className="text-muted-foreground text-xs">House No.</p><p className="font-medium">{house.house_no || '—'}</p></div>
                <div><p className="text-muted-foreground text-xs">Address</p><p className="font-medium">{house.address || '—'}{house.suburb ? `, ${house.suburb}` : ''}{house.postcode ? ` ${house.postcode}` : ''}</p></div>
                <Separator />
                <div className="grid grid-cols-3 gap-2">
                  <div><p className="text-muted-foreground text-xs">Capacity</p><p className="font-medium">{house.capacity || 0}</p></div>
                  <div><p className="text-muted-foreground text-xs">Occupied</p><p className="font-medium">{allocations.length}</p></div>
                  <div><p className="text-muted-foreground text-xs">Available</p><p className="font-medium">{Math.max(0, (house.capacity || 0) - allocations.length)}</p></div>
                </div>
                {house.accessible && <Badge variant="outline" className="text-xs">Accessible</Badge>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 px-3 pt-3"><CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4" /> Residents ({allocations.length})</CardTitle></CardHeader>
              <CardContent className="p-3 pt-0 space-y-1.5">
                {allocations.length > 0 ? allocations.map(a => (
                  <div key={a.id || a.participant_id} className="flex items-center justify-between bg-slate-50 rounded-md p-2">
                    <div>
                      <p className="text-xs font-medium">{a.participant_name || `Participant ${(a.participant_id || '').slice(-4)}`}</p>
                      {a.room_name && <p className="text-[10px] text-muted-foreground">Room: {a.room_name}</p>}
                    </div>
                    <Badge variant="outline" className="text-[10px]">{a.status}</Badge>
                  </div>
                )) : <p className="text-xs text-muted-foreground">No active residents</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 px-3 pt-3">
                <CardTitle className="text-sm flex items-center gap-2"><CalendarIcon className="w-4 h-4" /> Shifts in View ({houseShifts.length})</CardTitle>
                <p className="text-[10px] text-muted-foreground">{totalHours.toFixed(1)}h total</p>
              </CardHeader>
              <CardContent className="p-3 pt-0 space-y-1.5 max-h-72 overflow-y-auto">
                {houseShifts.length > 0 ? houseShifts.map(s => {
                  const style = getStatusStyle(s.status);
                  return (
                    <div key={s.id} onClick={() => { onShiftClick(s); }}
                      className={`rounded-md border px-2 py-1.5 cursor-pointer hover:shadow-sm ${style.bg}`}
                      data-testid={`sil-shift-${s.id}`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-[11px] font-semibold ${style.text}`}>{s.date} · {s.start_time}–{s.end_time}</span>
                        <Badge variant="outline" className="text-[9px]">{s.status}</Badge>
                      </div>
                      <p className="text-[10px] text-slate-600 truncate">{s.participant_name || '—'}</p>
                      <p className="text-[10px] text-slate-500 truncate">{s.assigned_staff?.map(a => a.name).join(', ') || 'Open shift'}</p>
                    </div>
                  );
                }) : <p className="text-xs text-muted-foreground">No shifts in this period</p>}
              </CardContent>
            </Card>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground mt-6">House not found</p>
        )}
      </SheetContent>
    </Sheet>
  );
}
